import type { Channel, NotifyLevel } from '@nexo/types';
import { cn, useToast } from '@nexo/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, BellOff, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { cache } from '../../lib/cache';
import { formatTime } from '../../lib/format';
import { useAlertPrefs } from './alerts';
import { isMuted } from './channelNotify';
import { errorMessage } from '../../lib/forms';

const FOREVER = '2999-12-31T23:59:59.000Z';

const muteOptions: { label: string; minutes: number | null }[] = [
  { label: '15 minutos', minutes: 15 },
  { label: '1 hora', minutes: 60 },
  { label: '8 horas', minutes: 8 * 60 },
  { label: '24 horas', minutes: 24 * 60 },
  { label: 'Hasta que lo reactive', minutes: null },
];

/** Discord-style notification settings of a conversation (for the header menu). */
export function ChannelNotifyMenu({ channel, onDone }: { channel: Channel; onDone: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const prefs = useAlertPrefs();
  const muted = isMuted(channel);
  const { t } = useTranslation();

  const save = useMutation({
    mutationFn: (input: { level?: NotifyLevel; mutedUntil?: string | null }) =>
      api.channels.setNotifications(channel.id, input),
    onMutate: (input) =>
      cache.patchChannel(queryClient, channel.teamId, channel.id, () => ({
        ...(input.level ? { notifyLevel: input.level } : {}),
        ...(input.mutedUntil !== undefined ? { mutedUntil: input.mutedUntil } : {}),
      })),
    onError: (err) => {
      cache.patchChannel(queryClient, channel.teamId, channel.id, () => ({
        notifyLevel: channel.notifyLevel,
        mutedUntil: channel.mutedUntil,
      }));
      toast({ title: t('No se pudo guardar'), description: errorMessage(err), tone: 'error' });
    },
  });

  const defaultLabel =
    channel.kind === 'dm'
      ? t('Todos los mensajes')
      : prefs.scope === 'mentions'
        ? t('Solo @menciones')
        : t('Todos los mensajes');
  const levels: { value: NotifyLevel; label: string; hint?: string }[] = [
    { value: 'default', label: t('Predeterminado'), hint: defaultLabel },
    { value: 'all', label: t('Todos los mensajes') },
    { value: 'mentions', label: t('Solo @menciones y respuestas') },
    { value: 'none', label: t('Nada') },
  ];

  return (
    <div className="text-sm">
      <p className="px-2.5 pt-2 pb-1 text-[11px] font-semibold tracking-wider text-subtle uppercase">
        {t('Notificaciones')}
      </p>
      {levels.map((level) => (
        <button
          key={level.value}
          type="button"
          role="menuitemradio"
          aria-checked={channel.notifyLevel === level.value}
          onClick={() => {
            save.mutate({ level: level.value });
            onDone();
          }}
          className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left hover:bg-surface-2"
        >
          <span className="w-4">
            {channel.notifyLevel === level.value && <Check className="size-4 text-accent" />}
          </span>
          <span className="flex-1">{level.label}</span>
          {level.hint && <span className="text-xs text-subtle">{level.hint}</span>}
        </button>
      ))}
      <div className="my-1 border-t border-border" />
      {muted ? (
        <button
          type="button"
          onClick={() => {
            save.mutate({ mutedUntil: null });
            onDone();
          }}
          className="flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left hover:bg-surface-2"
        >
          <Bell className="size-4 text-muted" />
          <span className="flex-1">{t('Dejar de silenciar')}</span>
          {channel.mutedUntil && channel.mutedUntil !== FOREVER && (
            <span className="text-xs text-subtle">
              {t('hasta {{time}}', { time: formatTime(channel.mutedUntil) })}
            </span>
          )}
        </button>
      ) : (
        <>
          <p className="flex items-center gap-2 px-2.5 pt-1 pb-1 text-[13px] text-muted">
            <BellOff className="size-4" /> {t('Silenciar')}
          </p>
          <div className="grid grid-cols-2 gap-1 px-1 pb-1">
            {muteOptions.map((option) => (
              <button
                key={option.label}
                type="button"
                onClick={() => {
                  save.mutate({
                    mutedUntil:
                      option.minutes === null
                        ? FOREVER
                        : new Date(Date.now() + option.minutes * 60_000).toISOString(),
                  });
                  onDone();
                }}
                className={cn(
                  'rounded-lg border border-border px-2 py-1.5 text-xs hover:bg-surface-2',
                  option.minutes === null && 'col-span-2',
                )}
              >
                {t(option.label)}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
